import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Volume2, VolumeX, ShoppingBag, X, Share2 } from 'lucide-react';
import { api, resolveImg, money } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

export default function Reels() {
  const [videos, setVideos] = useState([]);
  const [products, setProducts] = useState({});
  const [muted, setMuted] = useState(true);
  const navigate = useNavigate();
  const { add } = useCart();

  useEffect(() => {
    api.get('/videos').then(async ({ data }) => {
      setVideos(data);
      const map = {};
      await Promise.all(data.filter((v) => v.product_id).map(async (v) => {
        try { const { data: p } = await api.get(`/products/${v.product_id}`); map[v.product_id] = p; } catch {}
      }));
      setProducts(map);
    });
  }, []);

  const like = async (id) => {
    setVideos((vs) => vs.map((v) => v.id === id ? { ...v, likes: (v.likes || 0) + 1 } : v));
    try { await api.post(`/videos/${id}/like`); } catch {}
  };

  return (
    <div className="fixed inset-0 bg-black z-50 overflow-y-auto snap-y snap-mandatory">
      <button data-testid="reels-close" onClick={() => navigate('/')} className="fixed top-5 left-5 z-[60] w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white hover:bg-white/20">
        <X size={20} />
      </button>
      <button data-testid="reels-mute" onClick={() => setMuted((m) => !m)} className="fixed top-5 right-5 z-[60] w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white hover:bg-white/20">
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>

      {videos.length === 0 && <div className="h-screen flex items-center justify-center text-white/60 font-mono text-sm">No films available yet.</div>}

      {videos.map((v) => {
        const product = v.product_id ? products[v.product_id] : null;
        const igCode = v.instagram_url ? (v.instagram_url.match(/(?:reel|reels|p|tv)\/([A-Za-z0-9_-]+)/) || [])[1] : null;
        return (
          <div key={v.id} data-testid="reels-video-container" className="h-screen w-full snap-start relative flex items-center justify-center">
            {igCode ? (
              <iframe className="w-full max-w-[420px] h-[92vh] bg-white" src={`https://www.instagram.com/reel/${igCode}/embed/`} title={v.title} allow="autoplay; encrypted-media" scrolling="no" />
            ) : v.youtube_id ? (
              <iframe className="w-full max-w-md h-full aspect-[9/16]" src={`https://www.youtube.com/embed/${v.youtube_id}?autoplay=1&mute=1&loop=1&playlist=${v.youtube_id}&controls=0`} title={v.title} allow="autoplay" />
            ) : (
              <video className="w-full max-w-md h-full object-cover" src={resolveImg(v.video_url)} autoPlay loop muted={muted} playsInline />
            )}

            <div className="absolute inset-0 max-w-md mx-auto pointer-events-none bg-gradient-to-t from-black/80 via-transparent to-black/30" />

            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 w-full max-w-md px-6 flex items-end justify-between">
              <div className="text-white max-w-[70%]">
                <p className="font-serif text-2xl mb-2">{v.title}</p>
                {product && (
                  <>
                    <p className="font-mono text-gold text-sm mb-4">{money(product.price, product.currency)}</p>
                    <button data-testid="reels-buy-now-button" onClick={() => { add(product); }}
                      className="bg-gold text-primary-foreground px-6 py-3 text-xs font-mono uppercase tracking-widest flex items-center gap-2 hover:bg-gold-hover">
                      <ShoppingBag size={14} /> {product.is_presale ? 'Reserve' : 'Buy Now'}
                    </button>
                  </>
                )}
              </div>
              <div className="flex flex-col items-center gap-6 text-white">
                <button data-testid={`reels-like-${v.id}`} onClick={() => like(v.id)} className="flex flex-col items-center gap-1">
                  <span className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20"><Heart size={20} /></span>
                  <span className="text-xs font-mono">{v.likes || 0}</span>
                </button>
                <button onClick={() => { navigator.clipboard.writeText(window.location.origin + (product ? `/product/${product.id}` : '')); toast.success('Link copied'); }} className="flex flex-col items-center gap-1">
                  <span className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20"><Share2 size={18} /></span>
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
